import SectionHeading from "@/components/SectionHeading";
import AppButton from "@/components/AppButton";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Script } from "@/components/offline/ScriptCard";
import { Game } from "@/types/game";
import { Skeleton } from "@/components/ui/skeleton";
import CircularGallery from "@/components/gallery/CircularGallery";

// Import the same script data used in offline FindGamesSection
import { scriptData } from "@/components/offline/FindGamesSection";

const FeaturedGamesSection = () => {
  const { t } = useTranslation();
  
  // Convert Script data to Game format with explicit type as "offline" and limit to 10
  const convertedGames: Game[] = scriptData.map(script => ({
    id: script.id,
    title: script.title,
    description: script.description,
    image: script.image,
    type: "offline" as const,
    players: script.players,
    category: script.type,
    rating: 4.8,
    status: "published" as const,
    author: script.author || "AnzirAct Team",
    contentWarning: script.contentWarning,
    language: script.language,
    playTime: script.playTime,
    wordCount: script.wordCount,
    authorNotes: script.authorNotes,
    tertiaryType: script.tertiaryType
  })).slice(0, 10);
  
  // Convert games data to the format expected by CircularGallery
  const galleryItems = convertedGames.map(game => ({
    image: game.image,
    text: game.title,
    href: scriptData.find(script => script.id === game.id)?.link || `/game/${game.id}`
  }));
  
  const loading = false;
  const error = null;
  
  return (
    <motion.section 
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true, margin: "-100px" }}
      className="py-20 bg-black relative"
    >
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.05 }}
        transition={{ duration: 1 }}
        className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1582139329536-e7284fece509')] bg-cover bg-center opacity-5 mix-blend-overlay"
      ></motion.div>
      
      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <SectionHeading 
            title={t('featured.title')} 
            description={t('featured.description')} 
            centered 
            showPreTitle={false} 
          />
        </motion.div>

        {loading && (
          <div className="mt-10 flex gap-6 overflow-x-auto pb-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton 
                key={i} 
                className="rounded-lg flex-shrink-0 min-w-[240px] md:min-w-[260px] lg:min-w-[280px] aspect-[3/4]" 
              />
            ))}
          </div>
        )}

        {error && (
          <div className="mt-10 text-center py-8 bg-black/50 rounded-lg">
            <p className="text-red-400">Error loading featured games</p>
          </div>
        )}

        {!loading && !error && galleryItems.length > 0 && (
          <div style={{ height: '600px', position: 'relative', marginTop: '2rem', marginBottom: '2rem' }}>
            <CircularGallery 
              items={galleryItems}
              bend={0}
              textColor="#ffffff"
              borderRadius={0.05}
            />
          </div>
        )}

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <AppButton 
            to="/library" 
            variant="primary" 
            size="lg" 
            withArrow 
            className="bg-anzir hover:bg-anzir/80 transition-all duration-300 shadow-lg hover:shadow-anzir/20"
          >
            {t('featured.browse')}
          </AppButton>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default FeaturedGamesSection;
